import React, { memo, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { useTheme } from '../../../contexts/ThemeContext';

// Skeleton loading component
const ChartSkeleton = () => (
  <div className="h-64 animate-pulse">
    <div className="flex items-end justify-between h-full space-x-1 px-4 pb-4">
      {[...Array(24)].map((_, i) => (
        <div
          key={i}
          className="bg-gray-200 dark:bg-gray-700 rounded-t"
          style={{ height: `${Math.random() * 70 + 10}%`, width: '3%' }}
        />
      ))}
    </div>
    <div className="flex justify-center mt-4">
      <div className="h-3 w-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
    </div>
  </div>
);

// Custom tooltip component
const CustomTooltip = memo(({ active, payload, label, isDarkMode }) => {
  if (active && payload && payload.length) {
    return (
      <div 
        className={`p-3 rounded-lg border shadow-lg ${
          isDarkMode 
            ? 'bg-gray-800 border-gray-600 text-white' 
            : 'bg-white border-gray-200 text-gray-900'
        }`}
        role="tooltip"
        aria-label={`Taxa de resposta para ${label}`}
      >
        <p className="font-medium mb-1">{label}</p>
        <p className="text-blue-600 dark:text-blue-400 text-sm">
          Taxa de Resposta: {payload[0].value.toFixed(1)}%
        </p>
      </div>
    );
  }
  return null;
});

const ResponseTimeChart = memo(({ data, loading }) => {
  const { isDarkMode } = useTheme();

  // Memoizar dados processados
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    return data.map(item => ({
      hour: item.hour_label,
      taxa: parseFloat(item.response_rate) || 0,
      fullHour: item.hour_label // Para acessibilidade
    }));
  }, [data]);

  // Memoizar configurações do gráfico
  const chartConfig = useMemo(() => ({
    margin: { top: 5, right: 30, left: 20, bottom: 5 },
    barColor: "#3b82f6",
    barRadius: [4, 4, 0, 0]
  }), []);

  // Calcular estatísticas para acessibilidade
  const stats = useMemo(() => {
    if (!chartData.length) return null;
    
    const maxRate = Math.max(...chartData.map(d => d.taxa));
    const minRate = Math.min(...chartData.map(d => d.taxa));
    const avgRate = chartData.reduce((sum, d) => sum + d.taxa, 0) / chartData.length;
    const bestHour = chartData.find(d => d.taxa === maxRate)?.hour;
    
    return { maxRate, minRate, avgRate, bestHour };
  }, [chartData]);

  if (loading) {
    return <ChartSkeleton />;
  }

  if (!data || data.length === 0) {
    return (
      <div 
        className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400"
        role="img"
        aria-label="Nenhum dado de taxa de resposta disponível"
      >
        <svg className="w-12 h-12 mb-2 opacity-50" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
        <p>Nenhum dado disponível</p>
      </div>
    );
  }

  return (
    <div className="h-64">
      {/* Informações para leitores de tela */}
      {stats && (
        <div className="sr-only">
          Taxa de resposta por horário: Máxima {stats.maxRate.toFixed(1)}% às {stats.bestHour}, 
          Mínima {stats.minRate.toFixed(1)}%, Média {stats.avgRate.toFixed(1)}%
        </div>
      )}
      
      <div 
        role="img" 
        aria-label={`Gráfico de barras mostrando taxa de resposta por horário. ${stats ? `Melhor horário: ${stats.bestHour} com ${stats.maxRate.toFixed(1)}%` : ''}`}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={chartConfig.margin}
            aria-label="Gráfico de barras da taxa de resposta por horário"
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              className="opacity-30"
              stroke={isDarkMode ? '#374151' : '#e5e7eb'}
            />
            <XAxis 
              dataKey="hour" 
              tick={{ fontSize: 12 }}
              className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}
              stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
              interval={0}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}
              stroke={isDarkMode ? '#9ca3af' : '#6b7280'}
              label={{ value: 'Taxa (%)', angle: -90, position: 'insideLeft' }}
              domain={[0, 'dataMax + 5']}
            />
            <Tooltip content={<CustomTooltip isDarkMode={isDarkMode} />} />
            <Bar
              dataKey="taxa"
              fill={chartConfig.barColor}
              radius={chartConfig.barRadius}
              name="Taxa de Resposta"
              maxBarSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

export default ResponseTimeChart;